import { Select } from 'antd';
import type { DutyAnalyticsIdentityDto, OrgUnitSummaryDto } from '@pams/shared';
import { FilterField } from '@/components/FilterField';
import { withChosenOption } from '@/lib/selectOptions';
import { DUTY_ANALYTICS_COPY } from '../copy';

/**
 * [P7C §2] Narrows the commissionerate page's Units table to one division — `?division=`, which the
 * page sends to the server as `divisionId` once the server has said this caller's layout is the
 * commissionerate's.
 *
 * ⚠️ THE OPTIONS ARE THE DIVISIONS ROWS, SORTED BY NAME FOR FINDING — sorting a picker's options is not
 * re-sorting data. The root's own row is not a division and is never offered.
 *
 * ⚠️ A CHOSEN DIVISION WITH NO ROW IN THIS RANGE STILL HAS AN OPTION — the server's echoed `narrowedTo`
 * names it — so the control never shows a raw id ([D4.5 §1b], `lib/selectOptions.ts`).
 */
export const DivisionPicker = ({
  rows,
  value,
  narrowedTo,
  onChange,
}: {
  rows: readonly DutyAnalyticsIdentityDto[];
  value: string;
  narrowedTo: OrgUnitSummaryDto | null;
  onChange: (next: string | null) => void;
}) => {
  const options = rows
    .filter((row) => !row.isCommissionerate)
    .map((row) => ({ value: row.orgUnitId, label: row.orgUnit?.name ?? DUTY_ANALYTICS_COPY.unknownUnit }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const offered = withChosenOption(options, value, (id) => (narrowedTo?.id === id ? narrowedTo : undefined));
  return (
    <FilterField label={DUTY_ANALYTICS_COPY.pickerLabel} layout="inline">
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        className="w-64"
        data-testid="duty-analytics-division-picker"
        placeholder={DUTY_ANALYTICS_COPY.pickerPlaceholder}
        value={value === '' ? undefined : value}
        onChange={(next: string | undefined) => onChange(next ?? null)}
        options={offered}
      />
    </FilterField>
  );
};
